<script setup lang="ts">
import { v4 as uuid } from "uuid";
import { computed, nextTick, provide, ref, useTemplateRef, watch } from "vue";

import type { Action } from "@/action/types";
import type { Props as BasePopupProps } from "@/components/ui/BasePopup/BasePopup.vue";
import BasePopup from "@/components/ui/BasePopup/BasePopup.vue";
import { dropdownKey } from "@/components/ui/DropdownMenu/injectKeys";
import { usePopper } from "@/components/ui/DropdownMenu/usePopper";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { FeatureHighlightId } from "@/model/featureHighlight";
import { injectStrict } from "@/utils/vue";

import { moreMenuKey } from "../injectKeys";

const props = withDefaults(
  defineProps<{
    action: Action<never[]>;
    tooltip?: string;
    disabled?: boolean;
    width?: BasePopupProps["width"];
    maxHeight?: BasePopupProps["maxHeight"];
    highlight?: FeatureHighlightId;
  }>(),
  { tooltip: undefined, width: "md", maxHeight: "md", highlight: undefined },
);

const emit = defineEmits<{ open: [] }>();

const more = injectStrict(moreMenuKey);
const id = uuid();
const rowRef = useTemplateRef("row");
const contentRef = useTemplateRef("content");

const isOpen = computed(() => more.activeId.value === id);
// Hovering opens the flyout without the focus; a click or key on the row moves the focus in
const autofocus = ref(false);
const engaged = ref(false);
let tracked = false;

const popper = usePopper(() => ({
  placement: "right-start",
  // 4px up along the row, with a 2px gap from the row (same as the board context menu)
  offset: { left: -4, top: 2 },
  noFlip: false,
  fitContent: props.maxHeight === "content",
}));

// The flyout replaces the dropdown its content components were written for, so it answers their
// close() the same way: a pick dismisses the whole action menu.
provide(dropdownKey, { close: more.close, autofocus });

watch(isOpen, async (open) => {
  if (!open) {
    autofocus.value = false;
    engaged.value = false;
    tracked = false;
    popper.destroy();
    return;
  }
  await nextTick();
  popper.create(rowRef.value?.$el, contentRef.value?.$el);
});

function open() {
  if (props.disabled) return;

  autofocus.value = true;
  more.select(id);
  track();
}

// Clicks inside a hovered flyout start its trap first: before that they'd be outside clicks
// for the More menu's trap and deactivate it
function engage() {
  engaged.value = true;
  track();
}

// Counts the open once the user acts on it, so passing over the row is not a click
function track() {
  if (tracked) return;

  tracked = true;
  emit("open");
}

async function back() {
  more.select(null);
  await nextTick();
  rowRef.value!.focus();
}

// ArrowLeft stays with text inputs (search, number fields) to move the caret
function backUnlessTyping(event: KeyboardEvent) {
  if (!(event.target instanceof HTMLInputElement)) void back();
}
</script>

<template>
  <MenuListItem
    ref="row"
    :disabled
    :icon="action.data.icon"
    :text="$t(action.data.name)"
    :highlight
    :selected="isOpen"
    :tooltip="isOpen ? undefined : tooltip"
    :aria-controls="id"
    :aria-expanded="isOpen"
    @click="open"
    @keydown.right="open"
    @pointerenter="more.hover($event, disabled ? null : id)"
  >
    <template #after>
      <SvgIcon name="arrow/chevron-right" width="20" height="20" />
    </template>
  </MenuListItem>

  <Teleport v-if="isOpen" :to="more.host.value!">
    <!-- a hovered flyout's trap waits for the user to engage: until then the More menu's trap keeps
         handling Tab. Opened by click or key, it starts at once, or the More trap would pull the
         autofocus back out. -->
    <BasePopup
      :id
      ref="content"
      v-focus-trap="{ fallbackFocus: () => rowRef?.$el, deferred: !autofocus && !engaged }"
      v-scrollable-on-board
      class="submenu"
      :width
      :max-height
      tabindex="-1"
      @keydown.esc.stop="back"
      @keydown.left="backUnlessTyping"
      @keyup.esc.stop
      @pointerenter="more.hover($event, id)"
      @pointerleave="more.leave"
      @pointerdown.capture="engage"
    >
      <slot />
    </BasePopup>
  </Teleport>
</template>

<style lang="scss" scoped>
@use "@/styles/z-index";

.submenu {
  z-index: z-index.$popup-menu;
}
</style>
